import { useMemo, useState } from 'react';
import { usePublishersStore } from '../../../store/models';
import { useErrorStore } from '../../../store/models/error';
import useTriggeredErrorDialog from '../../../store/models/error/useTriggeredErrorDialog';
import { serviceErrorSerializer } from '../../../store/models/publishers/errors/serviceErrors';
import { AssetType } from '../../../utils/constants';

function useCoverPhoto () {
    const publishersStore = usePublishersStore();
    const errorStore = useErrorStore();

    // Shows the picked file immediately (before it's even sent to the backend),
    // so the UI doesn't wait on the upload+save round-trip to reflect the choice.
    const [previewUrl, setPreviewUrl] = useState(null);

    // Guards against reacting to an updatePublisherDetails failure triggered by
    // useEcalWorldProfile's own publicAccess toggle on the same page.
    const { renderErrorDialog, markTriggered, clearTriggered } = useTriggeredErrorDialog({
        hook: 'useCoverPhoto',
        serviceErrorSerializer,
        errorObservables: [
            errorStore.observed.errors.updatePublisherDetails,
            errorStore.observed.errors.uploadCoverPhoto,
        ],
    });

    // publisherDetails is fetched keyed to whichever publisher's token was last used
    // (see authGetPublisherInfo), so once it's loaded it's authoritative for this publisher.
    const coverPhoto = useMemo(() => {
        if (previewUrl !== null) {
            return previewUrl;
        }
        return publishersStore.publisherDetails?.coverPhoto || '';
    }, [previewUrl, publishersStore.observed.publisherDetails]);

    async function handleUpload (dataUrl, file) {
        setPreviewUrl(dataUrl);
        markTriggered();

        await publishersStore.uploadCoverPhoto({
            file,
            assetType: AssetType.PUBLISHER_COVER_PHOTO,
        });
        const uploadedAsset = publishersStore.assets[AssetType.PUBLISHER_COVER_PHOTO];
        const fileUrl = uploadedAsset?.fileUrl;
        if (fileUrl) {
            await publishersStore.updatePublisherDetailsRestricted({ coverPhoto: fileUrl });
        }
        clearTriggered();

        // publisherDetails.coverPhoto is authoritative again now that it's saved.
        setPreviewUrl(null);
    }

    async function handleDelete () {
        setPreviewUrl(null);
        markTriggered();
        await publishersStore.updatePublisherDetailsRestricted({ coverPhoto: '' });
        clearTriggered();
    }

    return {
        coverPhoto,
        handleUpload,
        handleDelete,
        renderErrorDialog,
    };
}

export default useCoverPhoto;
